import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import DefaultRoutes from './routing/defaultRoutes/DefaultRoutes';
import { SideBar, Loading, Settings, UserBalance, GameWrapper } from './components';
import { ContentTypes } from './core/constants/contentTypes';
import { EnvironmentTypes } from './core/constants/environmentTypes';
import { DefaultQueryParams } from './core/constants/defaultQueryParams';
import { parseQuery } from './core/helpers/parseQuery';
import { TranslationKeys } from './core/constants/translationKeys';
import rootRoutes from './routing/router';
import { SoundWatcher } from './containers/soundWatcher';
import { liveUpdateConnection } from './services/liveConnection/LiveUpdateConnection';
import { applicationStore } from './store/applicationStore';
import { popupStore } from './store/popupStore';
import { useLostInternet } from './hooks/useLostInternet';
import ConnectionWr from './containers/connectionWr';
import TopContentWrapper from './containers/topContentWrapper';
import PopupWrapper from './containers/popupWrapper';
import LiveBets from './containers/liveBets';
import ApplicationWrapper from './containers/applicationWrapper';
import { isPG, isTopC } from './core/helpers/skinHelper';
import { isMobile } from './core/helpers/isMobile';
import GameLogo from './components/gameLogo';
import './assets/styles/index.scss';
import { partnerSkinEnums, partnerSkinNames } from './core/constants/partnerSkinEnums';
import FreeBetWrapper from './components/freebetPopup';
import { LobbyWrapper } from './components/lobby';

const App = observer(() => {
  const { partnerSkin, isGameCustomView, setIsOverwriteCoreCssLoaded } = applicationStore;
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const { isOpenPopup } = popupStore;
  const { [DefaultQueryParams.GameId]: gameId } = parseQuery(search);

  const gameName = useMemo(() => {
    return rootRoutes.find((item) => item.gameId === +gameId)?.name.toLowerCase();
  }, [gameId]);

  const currentGameId = rootRoutes.find((el) => el.name === TranslationKeys.PowerOfGods).gameId;
  useEffect(() => {
    if (process.env.REACT_APP_ENV === EnvironmentTypes.Development) {
      let a = search.replace(gameId, currentGameId);
      if (!a.includes('gametypeid')) {
        a += '&gametypeid=430';
      }
      navigate(`${pathname}${a}`);
    }
  }, [currentGameId, navigate, pathname, search, gameId]);

  useEffect(() => {
    liveUpdateConnection.init().then(() => {
      liveUpdateConnection.sendGetInitialState();
    });
  }, []);
  const openConnection = useCallback(() => {
    liveUpdateConnection.stop();
    liveUpdateConnection.init().then(() => {
      liveUpdateConnection.sendGetInitialState();
    });
  }, []);

  useLostInternet(openConnection, () => {});

  useEffect(() => {
    if (partnerSkin !== partnerSkinEnums.pg) {
      import(`./providerStyles/${partnerSkinNames[partnerSkin]}_styles/index.scss`).then(() => {
        setIsOverwriteCoreCssLoaded(true);
      });
    }
  }, [partnerSkin, setIsOverwriteCoreCssLoaded]);
  return (
    <Loading>
      <ApplicationWrapper isOpenPopup={isOpenPopup}>
        <ConnectionWr />
        <TopContentWrapper gameName={gameName}>
          {!isGameCustomView && (
            <>
              {isTopC() || isMobile() ? (
                <UserBalance gameName={gameName} />
              ) : (
                <GameLogo gameName={gameName} />
              )}
              <SideBar>
                {isPG() && !isMobile() ? <UserBalance gameName={gameName} /> : null}
                <DefaultRoutes contentType={ContentTypes.BetContent} />
                <Settings />
              </SideBar>
            </>
          )}
          {isGameCustomView && isMobile() && (
            <>
              <UserBalance gameName={gameName} />
              <Settings />
            </>
          )}
          <GameWrapper gameId={gameId}>
            {isGameCustomView && !isMobile() && (
              <div className='balance-settings-container'>
                <Settings />
                <UserBalance gameName={gameName} />
              </div>
            )}
            <DefaultRoutes contentType={ContentTypes.GameContent} />
            <LobbyWrapper />
          </GameWrapper>
        </TopContentWrapper>
        <FreeBetWrapper />
        <LiveBets />
        <PopupWrapper />
      </ApplicationWrapper>
      <SoundWatcher />
    </Loading>
  );
});

export default App;
